import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import useTranscriptViewer, { getAuthenticatedMediaUrl, formatTime } from '../../hooks/useTranscriptViewer';
import useTranscriptSearch from '../../hooks/useTranscriptSearch';
import MediaPlayer from './MediaPlayer';
import TranscriptSearchBar from './TranscriptSearchBar';
import './Viewer.css';


function TranscriptViewer() {
  const { id } = useParams();
  const {
    transcript,
    loading,
    error,
    currentTime,
    activeUtteranceIndex,
    mediaPlayerRef,
    transcriptContainerRef,
    groupedUtterances,
    handleTimeUpdate,
    handleLineClick,
    handleDownload,
    handleDownloadMedia,
    copyAllText,
    handleWordClick,
  } = useTranscriptViewer(id);

  const search = useTranscriptSearch(groupedUtterances, handleWordClick);
  const searchInputRef = useRef(null);

  // Ctrl/Cmd+F focuses transcript search; pressing it again while the
  // search box is focused falls through to the browser's own find.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        const input = searchInputRef.current;
        if (input && document.activeElement !== input) {
          e.preventDefault();
          input.focus();
          input.select();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="viewer-container">
        <div className="loading">Loading transcript...</div>
      </div>
    );
  }

  if (error || !transcript) {
    return (
      <div className="viewer-container">
        <div className="alert alert-error">{error || 'Transcript not found'}</div>
        <Link to="/files" className="btn btn-secondary">Back to Files</Link>
      </div>
    );
  }

  if (transcript.status !== 'completed') {
    return (
      <div className="viewer-container">
        <div className="viewer-status card">
          <h2>{transcript.original_filename}</h2>
          <p className={`status-badge status-${transcript.status}`}>
            {transcript.status}
          </p>
          {transcript.status === 'processing' && (
            <p>Transcription in progress. Please wait...</p>
          )}
          {transcript.status === 'failed' && (
            <p>Transcription failed. Please try again.</p>
          )}
          <Link to="/files" className="btn btn-secondary">Back to Files</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-container">
      <div className="viewer-header">
        <div className="viewer-title-section">
          <Link to="/files" className="back-link">&larr; Back</Link>
          <h1 className="viewer-title">{transcript.original_filename}</h1>
        </div>

        <div className="viewer-actions">
          <button className="btn btn-secondary btn-sm" onClick={copyAllText}>
            Copy All
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => handleDownload('txt')}>
            Download .txt
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => handleDownload('json')}>
            Download .json
          </button>
          {transcript.media_url && (
            <button className="btn btn-secondary btn-sm" onClick={handleDownloadMedia}>
              Download Media
            </button>
          )}
        </div>
      </div>

      <div className="viewer-split-layout">
        <div className="viewer-video-panel">
          {transcript.media_url ? (
            <MediaPlayer
              ref={mediaPlayerRef}
              src={getAuthenticatedMediaUrl(transcript.media_url)}
              onTimeUpdate={handleTimeUpdate}
            />
          ) : (
            <div className="media-unavailable">The original media file is no longer available. The transcript can still be viewed and downloaded.</div>
          )}
        </div>

        <div
          ref={transcriptContainerRef}
          className="viewer-transcript-panel"
        >
          <div className="viewer-search-sticky">
            <TranscriptSearchBar
              ref={searchInputRef}
              search={search}
              placeholder="Search transcript (Ctrl+F)"
            />
          </div>
          <div className="transcript-content">
            {groupedUtterances.map((group, index) => {
              const currentMs = currentTime * 1000;
              const isActive = group.utterances.some((u) => u.originalIndex === activeUtteranceIndex);
              return (
                <div
                  key={index}
                  className={`paragraph-group ${isActive ? 'active' : ''}`}
                >
                  <div className="paragraph-header" onClick={() => handleLineClick(group.start)}>
                    <span className="speaker-label">Speaker {group.speaker}</span>
                    <span className="timestamp">{formatTime(group.start)}</span>
                  </div>
                  <p className="paragraph-text">
                    {group.words && group.words.length > 0 ? (
                      group.words.map((word, wi) => {
                        const isSpoken = currentMs >= word.start && currentMs < word.end;
                        return (
                          <span
                            key={wi}
                            className={`transcript-word${isSpoken ? ' word-active' : ''}${search.getWordSearchClass(index, wi)}`}
                            onClick={() => handleWordClick(word.start)}
                          >
                            {word.text}{' '}
                          </span>
                        );
                      })
                    ) : (
                      <span onClick={() => handleLineClick(group.start)}>{group.text}</span>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TranscriptViewer;
